/**
 * Matching a granted permission string against a required one.
 *
 * This is the browser-side twin of the API's
 * `src/common/permissions/permission-matcher.ts`, and the semantics must stay
 * identical: a UI that computes the answer differently from the server either
 * hides an affordance the user is allowed to use, or shows one that 403s.
 *
 * Permissions are `resource:action`. Three things satisfy a requirement:
 *   - `*:*` (or the bare `*` some older grants use) — satisfies everything
 *   - an exact match — `material:update` satisfies `material:update`
 *   - `resource:*` — `material:*` satisfies `material:update`
 *
 * Three hand-rolled copies of this lived in `useAuth.ts`, `PermissionMatrix.tsx`
 * and `UserPermissionsSection.tsx`, and they had already diverged: only two of
 * them honoured the bare `*`. Anything checking a permission calls this, never
 * `Array.includes` / `Set.has` directly.
 */
export function satisfiesPermission(
  grants: readonly string[] | ReadonlySet<string> | null | undefined,
  required: string,
): boolean {
  if (!grants || !required) return false;

  const holds = (permission: string) =>
    grants instanceof Set ? grants.has(permission) : (grants as readonly string[]).includes(permission);

  if (holds('*:*') || holds('*')) return true;
  if (holds(required)) return true;

  const [resource] = required.split(':');
  return !!resource && holds(`${resource}:*`);
}

/**
 * Every permission the API enforces, mirrored from
 * `yeschef-api/src/common/permissions/permission-catalogue.ts`.
 *
 * The API serves this list at `GET /roles/permissions`, and the permission
 * screen renders whatever it gets — so this copy is not what the UI draws. It
 * exists so `web-permission-vocabulary.test.ts` can check the strings this app
 * *gates on* offline, without a running API.
 *
 * Two tests hold the ends together: that unit test binds the gates to this
 * list, and `permission-vocabulary.spec.ts` in yeschef-playwright binds this
 * list to what the live API actually serves. Neither can drift silently.
 */
export const PERMISSION_CATALOGUE: readonly { resource: string; actions: string[] }[] = [
  { resource: 'organization', actions: ['read', 'create', 'update', 'delete'] },
  { resource: 'company', actions: ['read', 'create', 'update', 'delete'] },
  { resource: 'region', actions: ['read', 'create', 'update', 'delete'] },
  { resource: 'location', actions: ['read', 'create', 'update', 'delete'] },
  { resource: 'recipe', actions: ['read', 'create', 'update', 'delete'] },
  { resource: 'material', actions: ['read', 'create', 'update'] },
  { resource: 'menu', actions: ['read', 'write'] },
  { resource: 'inventory', actions: ['read', 'write'] },
  {
    resource: 'cooking-session',
    actions: ['read', 'create', 'update', 'delete', 'assign', 'cook'],
  },
  { resource: 'order', actions: ['read', 'create', 'update'] },
  { resource: 'user', actions: ['read', 'create', 'update', 'delete'] },
  { resource: 'role', actions: ['read', 'write', 'delete', 'assign'] },
  { resource: 'scope', actions: ['write'] },
  { resource: 'audit', actions: ['read'] },
];

export const ALL_PERMISSIONS: readonly string[] = PERMISSION_CATALOGUE.flatMap((group) =>
  group.actions.map((action) => `${group.resource}:${action}`),
);

/** Whether a string names something the API can actually decide on. */
export function isKnownPermission(permission: string): boolean {
  if (permission === '*:*' || permission === '*') return true;
  const [resource, action] = permission.split(':');
  if (!resource || !action) return false;
  const group = PERMISSION_CATALOGUE.find((entry) => entry.resource === resource);
  if (!group) return false;
  return action === '*' || group.actions.includes(action);
}

/**
 * Permissions this app gates on client-side, with no matching
 * `@RequirePermissions` decorator behind them. They are real — the Cook and
 * Assign buttons check them — but the API will not stop a hand-crafted request,
 * so the matrix marks them rather than presenting them as enforced access.
 *
 * Mirrors `UI_ONLY_PERMISSIONS` in
 * `yeschef-api/src/common/permissions/permission-catalogue.ts`.
 */
export const UI_ENFORCED_PERMISSIONS: readonly string[] = [
  'cooking-session:assign',
  'cooking-session:cook',
];

/**
 * Grants a role holds that name nothing in the catalogue, and therefore decide
 * nothing.
 *
 * Since the API's catalogue was made equal to the set it enforces, a grant with
 * no catalogue row is dead: either a system role edited by hand, or a custom
 * role assembled out of the old vocabulary the permission screen used to offer
 * (`company:write`, `menu:activate`, `recipe:publish`, and the rest). Restoring
 * the default roles clears them from system roles; a custom role has to be
 * edited.
 *
 * `*:*`, the bare `*` and `resource:*` are grants rather than catalogue rows —
 * a wildcard is live as long as its resource exists.
 */
export function findUnenforcedGrants(
  grants: readonly string[] | null | undefined,
  catalogue: readonly { resource: string; actions: string[] }[],
): string[] {
  if (!grants?.length) return [];

  return grants.filter((grant) => {
    if (grant === '*:*' || grant === '*') return false;

    const [resource, action] = grant.split(':');
    if (!resource || !action) return true;

    const group = catalogue.find((entry) => entry.resource === resource);
    if (!group) return true;

    return action !== '*' && !group.actions.includes(action);
  });
}
